import { Toast } from "@aurem/ui";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { AuthState, AuthStatus } from "./types";

export { AuthStatus } from "./types";
export type { AuthState, AuthTokens } from "./types";

export type AuthContextType = {
  authState: AuthState;
  setAuthState: (state: AuthState) => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

const AUTH_STORAGE_KEY_REFRESH = "AuremRefreshToken";

function setCookie(key: string, value: string): void {
  const date = new Date();
  date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000);
  document.cookie = `${key}=${value}; expires=${date.toUTCString()}; Secure; path=/; SameSite=Lax`;
  return;
}

function delCookie(key: string): void {
  const date = new Date();
  date.setTime(date.getTime() - 1 * 24 * 60 * 60 * 1000);
  document.cookie = `${key}=; expires=${date.toUTCString()}; Secure; path=/; SameSite=Lax`;
  return;
}

function getCookie(key: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const value = "; " + document.cookie;
  const parts = value.split("; " + key + "=");
  if (parts.length === 2) return parts.pop()?.split(";").shift();
  return undefined;
}

function persistSession(state: AuthState): void {
  try {
    if (state.status === AuthStatus.Unauthenticated) {
      return delCookie(AUTH_STORAGE_KEY_REFRESH);
    }
    if (state.refreshToken) {
      return setCookie(AUTH_STORAGE_KEY_REFRESH, state.refreshToken);
    }
    return;
  } catch (err) {
    Toast.error({ title: (err as Error).message });
    return;
  }
}

function retrieveAuth(): AuthState {
  const refreshToken = getCookie(AUTH_STORAGE_KEY_REFRESH);
  if (refreshToken) {
    return {
      refreshToken,
      accessToken: null,
      status: AuthStatus.Pending,
    };
  }
  return {
    refreshToken: null,
    accessToken: null,
    status: AuthStatus.Unauthenticated,
  };
}

type Props = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: Props) => {
  const [state, setState] = useState<AuthState>(() => retrieveAuth());

  const setAuthState = useCallback((newState: AuthState) => {
    setState((prev) => {
      if (
        prev.accessToken === newState.accessToken &&
        prev.refreshToken === newState.refreshToken &&
        prev.status === newState.status
      ) {
        return prev;
      }
      persistSession(newState);
      return newState;
    });
  }, []);

  const contextValue = useMemo<AuthContextType>(() => {
    return {
      authState: state,
      setAuthState,
    };
  }, [state, setAuthState]);

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
